import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getArchivedListNextPage } from '../../actions/archivedList';
import { getChatListNextPage } from '../../actions/chatList';
import { getSpamListNextPage } from '../../actions/spamList';
import { getStarredChatsListNextPage } from '../../actions/starredChatsList';
import { getUnreadListNextPage } from '../../actions/unreadList';
import { ChatList, ChatListLayout } from '../../components/ChatList';
import { ChatListSkeleton } from '../../components/ChatListSkeleton';
import { EmptyChatList } from '../../components/EmptyChatList';
import { useChatsToDisplay } from '../../hooks/useChatsToDisplay';
import { currentOpenChatIDSelector, chatListSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { ERoute } from '../../types/route';
import { LeftSwipeVisibleProvider } from '../../utils/useLeftSwipeVisible';
import { ChatListItemContainer } from '../ChatListItemContainer';
import { TopMenuContainer } from '../TopMenuContainer';

export const SubAgentChatListContainer: React.FC = () => {
  const dispatch = useDispatch();

  const { currentRoute, allChatsLoaded, status, isFirstLoad } = useSelector(chatListSelector);
  const currentOpenChatId = useSelector(currentOpenChatIDSelector);

  const chatsToDisplay = useChatsToDisplay();

  const getNextPage = React.useCallback(() => {
    switch (currentRoute) {
      case ERoute.Spam:
        return dispatch(getSpamListNextPage());
      case ERoute.Messages:
        return dispatch(getChatListNextPage());
      case ERoute.Archive:
        return dispatch(getArchivedListNextPage());
      case ERoute.Unread:
        return dispatch(getUnreadListNextPage());
      case ERoute.Starred:
        return dispatch(getStarredChatsListNextPage());
      default:
        return;
    }
  }, [dispatch, currentRoute]);

  const onScrollToEnd = React.useCallback(() => {
    if (allChatsLoaded || status === ERequestApiStatus.Pending) {
      return;
    }

    getNextPage();
  }, [allChatsLoaded, status, getNextPage]);

  React.useEffect(() => {
    if (isFirstLoad) {
      getNextPage();
    }
  }, [isFirstLoad, getNextPage]);

  const isChatListLoading = status === ERequestApiStatus.Pending && !chatsToDisplay.length;

  return (
    <ChatListLayout
      topMenu={<TopMenuContainer isChangeListVisible={false} />}
      chatListContent={
        <>
          {isChatListLoading && <ChatListSkeleton />}

          {!isChatListLoading && !chatsToDisplay.length && (
            <EmptyChatList route={currentRoute} withLinkButton={false} />
          )}

          {!isChatListLoading && chatsToDisplay.length > 0 && (
            <ChatList status={status} onScrollToEnd={onScrollToEnd}>
              <LeftSwipeVisibleProvider>
                {chatsToDisplay.map(chat => (
                  <ChatListItemContainer
                    chat={chat}
                    key={`chat-item-${chat.chatId}`}
                    isActive={chat.chatId === currentOpenChatId}
                  />
                ))}
              </LeftSwipeVisibleProvider>
            </ChatList>
          )}
        </>
      }
    />
  );
};
